import { css } from "@emotion/react";
import { rgba } from "polished";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { random } from "lodash";
import { useWhoAmI } from "./services/WhoAmI";
import Theme from "@hpo/client/utilities/Theme/client";
import T from "@hpo/client/components/text/Text";

type ImageDescriptior = { name: string; legend: string };

const images: Array<ImageDescriptior> = [
  {
    name: "ananas.jpg",
    legend: "la chambre d'agriculture de la Réunion",
  },
  {
    name: "goyavier1.jpg",
    legend: "l'association Nout Goyavier",
  },
  {
    name: "goyavier2.jpg",
    legend: "l'association Nout Goyavier",
  },
  {
    name: "legumes1.jpg",
    legend: "la chambre d'agriculture de la Réunion",
  },
  {
    name: "legumes2.jpg",
    legend: "la chambre d'agriculture de la Réunion",
  },
  {
    name: "mangue.jpg",
    legend: "la chambre d'agriculture de la Réunion",
  },
  {
    name: "plantation1.jpg",
    legend: "l'association Met zot an ler",
  },
  {
    name: "plantation2.jpg",
    legend: "l'association Met zot an ler",
  },
  {
    name: "riz1.jpg",
    legend: "l'association Riz Réunion",
  },
  {
    name: "riz2.jpg",
    legend: "l'association Riz Réunion",
  },
  {
    name: "riz3.jpg",
    legend: "l'association Riz Réunion",
  },
];

export default function Background() {
  const isLoggedIn = !!useWhoAmI().use();

  const [image, setImage] = useState(
    () => random(0, images.length - 1) as number,
  );

  useEffect(() => {
    const i = setInterval(
      () => {
        setImage(() => random(0, images.length - 1) as number);
      },
      1000 * 60 * 4,
    );
    return () => clearInterval(i);
  });

  const el = useMemo(() => {
    const existing = document.getElementById("background");
    if (existing) return existing;
    const el = document.createElement("div");
    el.id = "background";
    document.body.prepend(el);
    return el;
  }, []);

  const containerCss = css({
    position: "fixed",
    zIndex: -1,
    pointerEvents: "none",
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    background: "white",
  });

  const leftCss = css({
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "50%",
    background: Theme.color,
  });

  const backgroundImageCss = css({
    position: "absolute",
    top: 0,
    left: 0,
    zIndex: 10,
    width: "100%",
  });

  const pathCss = css({
    transition: "fill 500ms",
    fill: isLoggedIn ? Theme.background.loggedin : Theme.background.anonymous,
  });

  const rightColor1Css = css({
    position: "absolute",
    top: 0,
    right: 0,
    zIndex: 0,
    height: "100%",
    width: "50%",
    backgroundColor: Theme.background.loggedin,
  });

  const rightColor2Css = css({
    position: "absolute",
    top: 0,
    right: 0,
    zIndex: 1,
    height: "100%",
    width: "50%",
    backgroundColor: rgba(Theme.color, 0.1),
  });

  const rightImageCss = css({
    position: "absolute",
    top: 0,
    zIndex: 2,
    left: "50%",
    height: "100%",
    width: "50%",
    backgroundSize: "cover",
    backgroundPosition: "bottom right",
    transition: "opacity 500ms",
    opacity: isLoggedIn ? 0.2 : 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    alignItems: "flex-end",
    paddingBottom: 10,
  });

  const rightLegendCss = css({
    position: "absolute",
    zIndex: 2,
    bottom: 20,
    right: 20,
    width: "30vw",
    textAlign: "right",
    color: "white",
    textShadow:
      "0 0 10px black, 0 0 10px black, 0 0 10px black, 0 0 10px black",
    transition: "opacity 500ms",
    opacity: isLoggedIn ? 0 : 1,
  });

  return createPortal(
    <div css={containerCss}>
      <div css={leftCss}></div>
      <div css={rightColor1Css}></div>
      <div css={rightColor2Css}></div>
      {images.map((i, index) => (
        <div
          css={css(rightImageCss, {
            backgroundImage: `url(/backgrounds/${i.name})`,
            opacity: i === images[image] ? (isLoggedIn ? 0.5 : 1) : 0,
          })}
          key={index}
        ></div>
      ))}
      <div css={rightLegendCss}>
        <T style="minor" oneLine>
          Image fournie par
        </T>
        <T oneLine>{images[image].legend}</T>
      </div>
      <svg
        width="100%"
        viewBox="0 0 1920 1920"
        version="1.1"
        xmlns="http://www.w3.org/2000/svg"
        css={backgroundImageCss}
      >
        <g
          id="Artboard"
          stroke="none"
          strokeWidth="1"
          fill="none"
          fillRule="evenodd"
        >
          <path
            d="M1908.45289,-424 C1917.5089,-415.22151 1928.38963,-406.172913 1937.51323,-395.638726 C2260.4885,-20.4595835 2599.88624,339.323438 2954.62514,684.588189 C3432.36362,1149.64556 3932.53938,1588.23241 4480.36059,1970.36682 C4799.55126,2193.07034 5131.3798,2394.03023 5490.91703,2545.96563 C5554.10034,2572.60135 5618.67027,2595.93549 5683.28182,2619.18173 L5698.99825,2624.83619 L5709.47465,2628.60899 C5733.91723,2637.41808 5758.33974,2646.27675 5782.66933,2655.35911 C5792.53633,2659.07309 5802.20058,2663.25976 5812,2667.31137 L5811.30095,2670.0863 L5809.92818,2675.61084 C5809.69798,2676.533 5809.46567,2677.45727 5809.22913,2678.38577 C5671.6318,2678.38577 5534.03447,2678.11566 5396.50472,2678.38577 C4265.11133,2681.01932 3133.71794,2683.65286 2002.39213,2686.42146 C1680.43059,2687.23179 1358.53664,2688.17716 1036.64268,2689.66275 C878.500363,2690.40555 720.425628,2692.70146 562.350893,2694 C544.441613,2694.18705 526.532334,2692.36383 502,2691.08082 C618.197484,2433.72649 734.221601,2176.74749 850.139679,1920.00009 L1920,1920 L1920,0 L1717.00441,0.00105408326 C1428.01231,640.04744 1139.40615,1279.30266 850.139679,1920.00009 L-9.09494702e-13,1920 L0.00102777329,485.020583 C8.86612756,474.966817 15.137127,468.104077 20.6396447,460.664932 C54.6884854,414.18209 89.2102267,367.969497 122.110595,320.675908 C194.756934,215.812223 263.649568,108.791994 329.438173,0.00171246468 L1717.00441,0.00105408326 C1780.79801,-141.28607 1844.61042,-282.611747 1908.45289,-424 Z M7342.0448,-4667 C7342.49637,-4666.54831 7342.92705,-4666.11752 7343.33799,-4665.70646 L7344.78867,-4664.25518 C7345.06119,-4663.9825 7345.32341,-4663.72011 7345.57581,-4663.46749 L7346.53419,-4662.50805 C7346.83695,-4662.20486 7347.1234,-4661.91789 7347.39469,-4661.64597 L7347.79039,-4661.24926 C7350.68627,-4658.34505 7351.72052,-4657.28734 7352.5422,-4656.39464 L7352.69418,-4656.22878 L7352.80013,-4656.11229 L7352.8529,-4656.05403 C7354.13636,-4654.77025 7356.36553,-4653.41889 7356.36553,-4651.99997 C7357.31123,-2383.95496 7358.18939,-115.977527 7359,2151.99991 C7359,2164.83777 7357.78409,2177.60807 7356.83838,2197 C7334.54668,2193.48648 7315.09211,2191.79729 7296.31304,2187.27025 C7097.98447,2139.22962 6914.38192,2054.56729 6736.18343,1957.60764 C6436.0561,1794.22894 6162.2735,1592.94476 5902.60898,1371.7281 C5583.16217,1099.70054 5295.26149,796.52427 5017.49341,482.739868 C4733.84842,162.198697 4439.80064,-148.815429 4113.66633,-427.126788 C3999.093,-524.877731 3876.93308,-614.361524 3754.43599,-702.475685 L3745.88922,-708.621049 C3743.04018,-710.668821 3740.19105,-712.715939 3737.34191,-714.762489 C3652.76855,-775.370717 3559.54872,-822.668107 3457.47976,-849.154646 C3452.41346,-850.438432 3448.02267,-854.154656 3444.78024,-855.978984 C3497.15823,-868.380159 3549.68351,-880.453205 3602.24858,-892.533319 L3620.80243,-896.797728 L3639.35854,-901.066 C3741.42143,-924.558114 3843.53897,-948.37406 3944.92492,-974.965704 C4407.84921,-1096.45243 4855.57461,-1258.41221 5274.86121,-1492.39916 C5522.77193,-1630.84537 5753.32214,-1793.14299 5950.23215,-1999.69745 C6137.95528,-2196.58973 6280.35195,-2420.64423 6350.13173,-2686.11772 C6470.2367,-3143.07808 6321.69292,-3758.28199 5717.31767,-4045.17445 C5524.19049,-4136.86382 5320.05257,-4187.945 5108.82183,-4215.44506 C4856.37779,-4248.27891 4603.5592,-4240.65135 4350.6518,-4231.62156 L4333.60173,-4231.01113 L4308.02616,-4230.09333 C4290.97561,-4229.48227 4273.92473,-4228.87481 4256.87361,-4228.28292 C4217.89691,-4226.93157 4178.78511,-4227.2694 4139.74086,-4226.32346 C3903.6515,-4220.98561 3667.56214,-4215.58019 3431.60788,-4209.97207 C3323.72956,-4207.47207 3215.98635,-4204.43152 3108.17558,-4201.99908 C3091.82834,-4201.59368 3075.48109,-4203.28287 3054,-4204.29639 C3085.27593,-4361.25615 3115.8088,-4514.02673 3146.27413,-4667 L7342.0448,-4667 Z M1727,-3296 C1721.66298,-3275.19082 1718.82558,-3261.13787 1714.43436,-3247.55785 C1434.88256,-2389.85483 1115.40445,-1547.89381 723.167214,-734.781884 C602.232746,-484.087188 473.251894,-237.813563 329.438173,0.00171246468 L-9.09494702e-13,0 L0.00102777329,485.020583 C-2.88873975,488.297822 -6.05414924,491.91413 -9.55843427,496 C-11.585151,471.61002 -13.6118677,457.62463 -13.7469821,443.706803 C-15.3683555,250.276022 -17.3950722,56.7776788 -18.0030872,-136.653102 C-19.6244606,-692.893276 -20.5702617,-1249.13345 -22.0565206,-1805.37362 C-22.1325225,-1830.26188 -22.2021909,-1855.14907 -22.2719912,-1880.03521 L-22.3775989,-1917.36363 C-22.8426651,-2079.11668 -23.4498884,-2240.82513 -25.9748396,-2402.48897 C-26.6504119,-2443.70196 -13.8145394,-2464.44357 23.8148342,-2482.41514 C419.511005,-2671.25439 814.786915,-2861.07431 1210.0208,-3050.94188 L1341.76439,-3114.23155 C1451.55028,-3166.97228 1561.33618,-3219.70868 1671.13018,-3272.42077 C1687.14124,-3280.12287 1703.89543,-3286.3386 1727,-3296 Z M4046.49896,-3727.71333 C4270.28017,-3724.0645 4491.49307,-3697.03607 4699.99958,-3607.63954 C4749.33823,-3586.4898 4797.46032,-3561.28579 4842.9465,-3532.83837 C5010.2924,-3428.3735 5119.64849,-3280.055 5161.14703,-3086.80174 C5217.44717,-2823.95028 5193.92682,-2571.36962 5039.49007,-2343.45241 C4984.47409,-2262.23198 4922.69939,-2184.25497 4855.31496,-2113.03506 C4680.66964,-1928.36333 4467.83755,-1795.9916 4244.39427,-1678.48551 C4099.9919,-1602.62201 3947.31113,-1548.81451 3794.02257,-1496.48574 L3783.91483,-1493.03742 C3782.23009,-1492.46298 3780.54529,-1491.88867 3778.86045,-1491.31445 L3768.75099,-1487.8701 C3762.01111,-1485.57435 3755.27088,-1483.27926 3748.53093,-1480.98311 L3737.04279,-1477.06784 C3717.89676,-1470.5396 3698.75606,-1463.99467 3679.63564,-1457.39297 C3174.28616,-1283.05961 2668.53116,-1109.80739 2162.91133,-936.149745 C2162.45073,-935.991245 2161.98993,-935.833321 2161.52878,-935.675904 L2160.14409,-935.205096 C2142.35645,-929.180697 2123.82006,-923.800089 2096,-915 C2157.90467,-1103.5567 2216.59641,-1282.26272 2274.83974,-1459.8386 L2279.15328,-1472.99044 C2284.90369,-1490.52381 2290.65039,-1508.04826 2296.39604,-1525.57218 C2318.4565,-1592.82567 2340.52035,-1660.0778 2362.58588,-1727.32919 L2428.78585,-1929.08174 C2605.32361,-2467.08584 2781.86137,-3005.08129 2957.53402,-3543.37946 C2970.24041,-3582.50311 2986.25858,-3603.65285 3030.12267,-3613.24794 C3231.73528,-3657.37185 3434.15894,-3695.34679 3640.09713,-3709.87457 C3775.33912,-3719.40209 3911.18938,-3729.87561 4046.49896,-3727.71333 Z M2257,-4667 C2256.39184,-4654.29719 2257.60816,-4642.94574 2254.97281,-4632.67538 L2251.01419,-4617.12575 L2247.05401,-4601.57651 C2205.671,-4439.17937 2163.42103,-4277.0318 2101.10938,-4120.91425 C2098.33889,-4114.02229 2095.23054,-4107.19791 2092.79792,-4100.17082 C2069.75557,-4031.45402 2025.69805,-3988.68338 1956.63858,-3958.41285 C1513.24143,-3764.1987 1070.56715,-3568.24323 627.951279,-3372.12726 L525.80989,-3326.86759 C508.786397,-3319.32408 491.762915,-3311.78051 474.739406,-3304.23697 L368.949774,-3257.3605 C263.159122,-3210.48603 157.364402,-3163.62002 51.5565442,-3116.78403 C29.7305059,-3107.12179 7.83689482,-3097.66224 -14.3270078,-3088 C-27.2334267,-3129.21657 -30.6796433,-4596.86426 -18.3138073,-4667 L2257,-4667 Z"
            id="Combined-Shape"
            fill="#FFFFFF"
            css={pathCss}
          ></path>
        </g>
      </svg>
    </div>,
    el,
  );
}
